import { TicketActions, TicketEditorFormProps } from '@/routes/tickets/TicketEditor.types'
import { ArraySourceComposer } from '@/auth/api/connector'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import {
  argomento_problema,
  azienda,
  contatto_aziendale,
  email_aziendale,
  piattaforma_ticket,
  qs_supporto_applicazione,
  sede,
  stato_ticket,
  ticket,
} from '@/model/qsadminapi/QsAdminApiModuleModel'
import {
  ButtonItem,
  ButtonOptions,
  CustomRule,
  Form as DXForm,
  EmailRule,
  FormRef,
  GroupItem,
  RequiredRule,
  SimpleItem,
  Tab,
  TabbedItem,
  TabPanelOptions,
} from 'devextreme-react/form'
import { FieldDataChangedEvent } from 'devextreme/ui/form'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ValueChangedEvent } from 'devextreme/ui/lookup'
import { ValueChangedEvent as ValueChangedEventSelectBox } from 'devextreme/ui/select_box'
import { ValueChangedEvent as ValueChangedEventTagBox } from 'devextreme/ui/tag_box'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { ClickEvent } from 'devextreme/ui/button'
import { StatoTicket, TipologiaTicket } from '@/routes/tickets/TicketEditor.enums'
import { ValidationCallbackData } from 'devextreme/common'
import { Button, DropDownButton, Popup, ScrollView, TextBox } from 'devextreme-react'
import './TicketEditor.form.scss'
import NoteView from '@/routes/tickets/notes/NoteView'
import {
  canEditTicketNote,
  createTicketNoteDraft,
  getTicketNoteQueryKey,
  TicketNoteTicketContext,
  TicketNoteViewModel,
  useTicketNotes,
} from '@/routes/tickets/notes/ticketNotes'
import TicketEditorEvents from '@/routes/tickets/TIcketEditor.events'
import AttachmentsManager from '@/components/file-manager/AttachmentsManager'
import { v4 as uuidv4 } from 'uuid'
import { confirm } from 'devextreme/ui/dialog'
import {
  customButton,
  isUserRoleAllowed,
  onInitializedHtmlEditor,
  onValueChangedHtmlEditor,
  toolbarHtmlEditor,
} from '@/routes/utils'
import { PopupRef } from 'devextreme-react/popup'
import { FileManagerRef } from 'devextreme-react/file-manager'
import FormPopupClientNote from '@/components/form-popup-client-note/FormPopupClientNote'
import notify from 'devextreme/ui/notify'
import { Roles } from '@/auth/azure/Roles'
import { AssignSelector } from '@/routes/tickets/AssignSelector'
import { useScreenSize } from '@/themes/media-query'
import DropDownBoxDataGridItem from '@/components/ddb-datagrid-item/DropDownBoxDataGridItem'
import { TextBoxRef, TextBoxTypes } from 'devextreme-react/cjs/text-box'
import { TagBoxTypes } from 'devextreme-react/cjs/tag-box'
import TicketEditorDumps from '@/routes/tickets/TicketEditor.dumps'
import {
  createTicketAttachmentFolder,
  duplicateTicketWithApi,
  getTicketAttachmentPermissions,
} from '@/routes/tickets/ticketWrite'
import {
  useTicketTakeChargeMutation,
  useTicketReleaseMutation,
  useTicketRemoveAssigneeMutation,
  useTicketAssignToMutation,
  useTicketReopenMutation,
  useTicketCancelMutation,
  useTicketSendClosureNoticeMutation,
  useTicketSendContactNoticeMutation,
  useTicketSendSupportExpiredNoticeMutation,
  useTicketSendOtherVendorNoticeMutation,
  AssignToMutationParams,
  useTicketSetActivityRequestMutation,
  useTicketSendSupportSuspendedNoticeMutation,
} from '@/routes/tickets/utils/mutations'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import TicketEditorPopup from '@/routes/tickets/TicketEditor.popup'
import { readTicketEditorDraft } from '@/routes/tickets/ticketEditorDraftStorage'
import {
  buildAdditionalEmailDataSourceItems,
  normalizeAdditionalEmailItems,
  resolveAdditionalEmailCustomItem,
} from '@/routes/tickets/utils/utils'

const NON_PRESENT_VALUE = '------- non presente -------'

const TicketEditorForm = (props: TicketEditorFormProps) => {
  const {
    creating,
    ticket,
    states,
    platforms,
    applications,
    clients,
    problemCategories,
    categories,
    employees,
    problemArguments,
    isRoleAllowed,
    userInfo,
    isBusy,
    onSaveTicket,
    onResetTicket,
    onRefreshTicket,
  } = props
  const { clientId } = useParams()
  const { service, client } = useQsAdminApiManager()

  const popupActionRef = useRef<PopupRef>(null)
  const popupNoteClienteRef = useRef<PopupRef>(null)
  const fileManagerRef = useRef<FileManagerRef>(null)

  const currentScreenSize = useScreenSize()
  const formRef = useRef<FormRef>(null)
  const takeChargeMutation = useTicketTakeChargeMutation(clientId)
  const releaseMutation = useTicketReleaseMutation(clientId)
  const removeAssigneeMutation = useTicketRemoveAssigneeMutation(clientId)
  const assignToMutation = useTicketAssignToMutation(clientId)
  const reopenMutation = useTicketReopenMutation(clientId)
  const cancelMutation = useTicketCancelMutation(clientId)
  const sendClosureNoticeMutation = useTicketSendClosureNoticeMutation(clientId)
  const sendContactNoticeMutation = useTicketSendContactNoticeMutation(clientId)
  const sendSupportExpiredNoticeMutation = useTicketSendSupportExpiredNoticeMutation(clientId)
  const sendSupportSuspendedNoticeMutation = useTicketSendSupportSuspendedNoticeMutation(clientId)
  const sendOtherVendorNoticeMutation = useTicketSendOtherVendorNoticeMutation(clientId)
  const setActivityRequestMutation = useTicketSetActivityRequestMutation(clientId)
  const queryClient = useQueryClient()

  const ticketNoteContext: TicketNoteTicketContext | null =
    ticket && !creating
      ? {
          id: ticket.id,
          ded_Dis: ticket.ded_Dis,
          attachmentsFolderUrl: ticket.attachmentsFolderUrl,
        }
      : null

  const ticketNotesQuery = useTicketNotes(ticketNoteContext)
  const ticketNotes = ticketNotesQuery.data ?? []

  const [popup, setPopup] = useState<{
    visible: boolean
    action: TicketActions
    ticket?: ticket | null
    complete?: { ticket: ticket | null | undefined; closure: string; solution: string }
    note?: TicketNoteViewModel
  }>({
    visible: false,
    action: TicketActions.None,
  })
  const textBoxAssistenzaTelefonicaRef = useRef<TextBoxRef>(null)
  const [loadPanelVisible, setLoadPanelVisible] = useState(false)
  const [currentCompanyChanged, setCurrentCompanyChanged] = useState<boolean>(clientId && creating ? true : false)

  const [currentCompany, setCurrentCompany] = useState<azienda | null | undefined>(
    clientId ? clients[0] : ticket?.sede?.azienda,
  )
  const { data: impiegatiData } = useQuery({
    queryKey: ['impiegati'],
    queryFn: async () => {
      const result = await useQsAdminApiManager.getState().service.user().query()
      return result.data.value
    },
  })
  const additionalEmailDataSourceItems = useMemo(
    () => buildAdditionalEmailDataSourceItems(ticket?.email_addizionali, employees),
    [employees, ticket?.email_addizionali],
  )

  const [sediList, setSediList] = useState<sede[] | undefined>(ticket?.sede?.azienda?.sedi)
  const [problemArgumentsList, setProblemArgumentsList] = useState<argomento_problema[] | undefined>(problemArguments)

  const popupAssignRef = useRef<PopupRef>(null)

  const getCompanySites = useCallback(
    async (companyId: number) => {
      const sitesData = await service.sede().query((builder, sede) => {
        builder.filter(sede.azienda.props.id.eq(companyId))
        builder.orderBy(sede.principale.desc())
        builder.expanding('azienda', (aziendaSedeBuilder, azienda) => {
          aziendaSedeBuilder.expanding('sedi', (sediAziendaSedeBuilder, sedeAzienda) => {
            sediAziendaSedeBuilder.expanding('assistenze', (assistenzeBuilder, assistenza) => {
              assistenzeBuilder.expanding('telefonica', (telefonicaBuilder, telefonica) => {
                telefonicaBuilder.select('id', 'nome')
              })
              assistenzeBuilder.select('id', 'sospesa', 'telefonica')
            })
            sediAziendaSedeBuilder.expanding('contatti_aziendali', (contattoBuilder, contatto) => {
              contattoBuilder.select('id', 'fullname', 'responsabile_tecnico')
            })
            sediAziendaSedeBuilder.select('id', 'nome', 'note', 'assistenze', 'contatti_aziendali')
            sediAziendaSedeBuilder.orderBy(sedeAzienda.principale.desc())
          })
          aziendaSedeBuilder.select('id', 'nome', 'sedi')
        })
        builder.expanding('assistenze', (assistenzeBuilder, assistenza) => {
          assistenzeBuilder.expanding('telefonica', (telefonicaBuilder, telefonica) => {
            telefonicaBuilder.select('id', 'nome')
          })
          assistenzeBuilder.select('id', 'sospesa', 'telefonica')
        })
        builder.expanding('contatti_aziendali', (contattoBuilder, contatto) => {
          contattoBuilder.select('id', 'fullname', 'responsabile_tecnico')
        })
        builder.select('id', 'nome', 'note', 'azienda', 'assistenze', 'contatti_aziendali')
      })
      return sitesData.data.value
    },
    [service],
  )

  const setFormDirty = useCallback((isDirty: boolean) => {
    const formInstance = formRef.current?.instance()
    formInstance?.option('isDirty', isDirty)
    formInstance?.getButton('btn-save')?.option('disabled', !isDirty)
    formInstance?.getButton('btn-cancel')?.option('disabled', !isDirty)
  }, [])

  const getFilteredProblemArguments = useCallback(
    (applicationName: string | null | undefined, platformName: string | null | undefined) => {
      if (!applicationName && !platformName) return []

      const filtered = (problemArguments ?? []).filter((problemArgument) => {
        if (applicationName && applicationName !== NON_PRESENT_VALUE) {
          return problemArgument.qs_applicazione?.nome === applicationName
        }

        return problemArgument.piattaforma?.nome === (platformName ?? '')
      })

      return [...new Map(filtered.map((problemArgument) => [problemArgument.id, problemArgument])).values()]
    },
    [problemArguments],
  )

  const setProblemArgumentsIfChanged = useCallback((nextArguments: argomento_problema[]) => {
    setProblemArgumentsList((previousArguments) => {
      const previousList = previousArguments ?? []
      if (
        previousList.length === nextArguments.length &&
        previousList.every((argument, index) => argument.id === nextArguments[index]?.id)
      ) {
        return previousArguments
      }
      return nextArguments
    })
  }, [])

  useEffect(() => {
    if (!currentCompanyChanged) return
    console.log('COMPANY EFFECT', currentCompany)
    if (currentCompany) {
      getCompanySites(currentCompany.id).then((sites: sede[]) => {
        console.log('SITES', sites)
        setSediList(sites)
        formRef.current?.instance().updateData('sede', sites[0])
      })
      setCurrentCompanyChanged(false)
    }
  }, [currentCompanyChanged, currentCompany, getCompanySites])

  useEffect(() => {
    if (!ticket?.id) return

    const channel = new BroadcastChannel(`ticket-${ticket.id}`)

    channel.onmessage = (event) => {
      if (event.data.type === 'note-added') {
        queryClient.invalidateQueries({ queryKey: getTicketNoteQueryKey(ticket.id) })
        return
      }

      if (event.data.type === 'ticket-updated') {
        console.log('Ticket updated from resolving page, reloading...')
        queryClient.invalidateQueries({ queryKey: getTicketNoteQueryKey(ticket.id) })
        onRefreshTicket?.()
      }
    }

    return () => {
      channel.close()
    }
  }, [onRefreshTicket, queryClient, ticket?.ded_Dis, ticket?.id])

  const getCurrentTicketData = useCallback(() => {
    const formData = formRef.current?.instance().option('formData') as ticket | undefined
    const currentTicket = formData ?? ticket ?? null
    if (!currentTicket) return null

    return {
      ...currentTicket,
      email_addizionali: normalizeAdditionalEmailItems(currentTicket.email_addizionali ?? []),
    }
  }, [ticket])

  const updateTicketField = useCallback(
    <K extends keyof ticket>(field: K, value: ticket[K]) => {
      formRef.current?.instance().updateData(field as string, value)
      if (ticket) ticket[field] = value
      setFormDirty(true)
    },
    [setFormDirty, ticket],
  )

  const refreshProblemArgumentsForSelection = useCallback(
    (applicationName: string | null | undefined, platformName: string | null | undefined) => {
      const filteredArguments = getFilteredProblemArguments(applicationName, platformName)
      setProblemArgumentsIfChanged(filteredArguments)

      const currentTicket = getCurrentTicketData()
      const currentArgument = currentTicket?.argomento_problema
      if (!currentArgument) return

      const stillValid = filteredArguments.some((argument) => argument.id === currentArgument.id)
      if (!stillValid) {
        updateTicketField('argomento_problema', null)
      }
    },
    [getCurrentTicketData, getFilteredProblemArguments, setProblemArgumentsIfChanged, updateTicketField],
  )

  useEffect(() => {
    const currentTicket = getCurrentTicketData()
    const nextArguments = getFilteredProblemArguments(
      currentTicket?.applicazione?.nome,
      currentTicket?.piattaforma?.nome,
    )
    setProblemArgumentsIfChanged(nextArguments)
  }, [getCurrentTicketData, getFilteredProblemArguments, problemArguments, setProblemArgumentsIfChanged])

  const renderTicketNotes = () => {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Button
            icon={ticketNotesQuery.isFetching ? 'refresh' : 'revert'}
            text="Aggiorna note"
            stylingMode="text"
            disabled={ticketNotesQuery.isFetching}
            onClick={() => {
              ticketNotesQuery.refetch()
            }}
          />
        </div>
        {ticketNotes.length > 0 ? (
          <div className="notes-list">
            {ticketNotes.map((nota) => {
              const canEditNote = canEditTicketNote(nota, userInfo?.name)
              return (
                <NoteView
                  key={nota.id}
                  note={nota}
                  readOnly={!isRoleAllowed || !canEditNote}
                  visibleEditClick={true}
                  onEditClick={(e: ClickEvent) => {
                    setPopup({
                      visible: true,
                      action:
                        !isRoleAllowed || !canEditNote ? TicketActions.ShowTicketNote : TicketActions.EditTicketNote,
                      note: nota,
                      ticket,
                    })
                    popupActionRef.current?.instance().show()
                  }}
                />
              )
            })}
          </div>
        ) : ticketNotesQuery.isLoading ? (
          <div className="no-notes-message">
            <i className="dx-icon-spin dx-icon-refresh"></i>
            <p>Caricamento note in corso...</p>
          </div>
        ) : ticketNotesQuery.isError ? (
          <div className="no-notes-message">
            <i className="dx-icon-warning"></i>
            <p>Errore durante il caricamento delle note</p>
          </div>
        ) : (
          <div className="no-notes-message">
            <i className="dx-icon-info"></i>
            <p>Nessuna nota associata al ticket</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <LoadingPanel visible={!!(isBusy || loadPanelVisible)} />
      <DXForm
        id={'ticket_form'}
        formData={ticket}
        labelLocation={'top'}
        showValidationSummary={true}
        onFieldDataChanged={(e: FieldDataChangedEvent) => {
          if (
            formRef.current?.instance().option().isDirty !== null &&
            formRef.current?.instance().option().isDirty !== undefined
          ) {
            formRef.current
              .instance()
              .getButton('btn-save')
              ?.option('disabled', !formRef.current?.instance().option().isDirty)
            formRef.current
              .instance()
              .getButton('btn-cancel')
              ?.option('disabled', !formRef.current?.instance().option().isDirty)
          }
        }}
        validationGroup="ticketValidation"
        ref={formRef}
        readOnly={
          !!(
            !isRoleAllowed ||
            (!creating && ticket?.stato?.id === StatoTicket.APERTO) ||
            (ticket?.assegnato && ticket?.assegnato.email !== userInfo?.email)
          )
        }
      >
        <GroupItem colCountByScreen={{ xs: 1, sm: 2, md: 2, lg: 2 }}>
          <GroupItem colCount={1}>
            <GroupItem caption={`Dati Cliente`}>
              <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 5 }}>
                <SimpleItem
                  colSpan={2}
                  label={{ text: 'Azienda' }}
                  editorType="dxSelectBox"
                  editorOptions={{
                    buttons: [
                      customButton('open', (e: ClickEvent) => {
                        if (currentCompany) window.open(`/clients/${currentCompany.id}`, '_blank')
                        else notify(`Deve essere selezionata un' azienda per poterla aprire.`, 'warning', 3000)
                      }),
                      { name: 'dropDown', location: 'after' },
                    ],
                    value: currentCompany,
                    dataSource: ArraySourceComposer('id', clients),
                    placeholder: "Seleziona l'azienda...",
                    displayExpr: 'nome',
                    searchEnabled: true,
                    readOnly:
                      !isRoleAllowed ||
                      (clientId
                        ? true
                        : (!creating && ticket?.stato?.id === StatoTicket.APERTO) ||
                            (ticket?.assegnato && ticket?.assegnato.email !== userInfo?.email)
                          ? true
                          : false),
                    dropDownOptions: {
                      showTitle: true,
                      title: "Selezionare l'azienda",
                      hideOnOutsideClick: true,
                      width: currentScreenSize.isLarge ? '150%' : '',
                      resizeEnabled: currentScreenSize.isLarge ? true : false,
                    },
                    showClearButton: true,
                    onValueChanged: (e: ValueChangedEventSelectBox) => {
                      if (e.previousValue?.id === e.value?.id) return
                      console.log('COMPANY EVENT CHANGED', e.previousValue, e.value)
                      if (e.value) {
                        setCurrentCompany(e.value)
                        setCurrentCompanyChanged(true)
                      }
                    },
                  }}
                >
                  <CustomRule
                    message="Azienda obbligatoria se stato 'ASSEGNATO'"
                    validationCallback={({ value }: ValidationCallbackData) => {
                      return ticket?.stato?.id === StatoTicket.ASSEGNATO && !value ? false : true
                    }}
                  />
                  <CustomRule
                    message="Azienda obbligatoria se stato 'COMPLETATO'"
                    validationCallback={({ value }: ValidationCallbackData) => {
                      return ticket?.stato?.id === StatoTicket.COMPLETATO && !value ? false : true
                    }}
                  />
                </SimpleItem>
                <SimpleItem
                  dataField="sede"
                  label={{ text: 'Sede' }}
                  editorType="dxSelectBox"
                  editorOptions={{
                    buttons: [
                      customButton(
                        'info',
                        () => {
                          console.log('ticket.sede', ticket?.sede)
                          if (ticket?.sede) {
                            popupNoteClienteRef.current?.instance().show()
                          } else notify('Deve essere selezionata una sede per aprire le note.', 'warning', 3000)
                        },
                        false,
                        'Note cliente',
                      ),
                      {
                        name: 'clear',
                        location: 'after',
                      },
                      { name: 'dropDown', location: 'after' },
                    ],
                    dataSource: ArraySourceComposer('id', sediList),
                    placeholder: 'Seleziona la sede...',
                    displayExpr: 'nome',
                    searchEnabled: true,
                    showDropDownButton: true,
                    dropDownButtonTemplate: 'dropDownButton',
                    dropDownOptions: {
                      showTitle: true,
                      title: 'Selezionare la sede',
                      hideOnOutsideClick: true,
                    },
                    readOnly:
                      !isRoleAllowed ||
                      (!creating && ticket?.stato?.id === StatoTicket.APERTO) ||
                      (ticket?.assegnato && ticket?.assegnato.email !== userInfo?.email)
                        ? true
                        : false,
                    onValueChanged: (e: ValueChangedEventSelectBox) => {
                      if (e.previousValue?.id === e.value?.id) return
                      console.log('SITE EVENT CHANGED', e.previousValue, e.value)
                      textBoxAssistenzaTelefonicaRef.current
                        ?.instance()
                        .option(
                          'value',
                          e.value.assistenze?.[0]?.sospesa &&
                            (e.value.assistenze?.[0]?.telefonica?.nome === 'STANDARD' ||
                              e.value.assistenze?.[0]?.telefonica?.nome === 'PRIME')
                            ? 'SOSPESA'
                            : (e.value.assistenze?.[0]?.telefonica?.nome ?? ''),
                        )
                    },
                  }}
                >
                  <CustomRule
                    message="Sede obbligatoria se stato 'ASSEGNATO'"
                    validationCallback={({ value }: ValidationCallbackData) => {
                      return ticket?.stato?.id === StatoTicket.ASSEGNATO && !value ? false : true
                    }}
                  ></CustomRule>
                  <CustomRule
                    message="Sede obbligatoria se stato 'COMPLETATO'"
                    validationCallback={({ value }: ValidationCallbackData) => {
                      return ticket?.stato?.id === StatoTicket.COMPLETATO && !value ? false : true
                    }}
                  ></CustomRule>
                </SimpleItem>
                <SimpleItem label={{ text: 'Assistenza Telefonica' }}>
                  <TextBox
                    ref={textBoxAssistenzaTelefonicaRef}
                    readOnly={true}
                    className={`dx-texteditor-input assistenza-telefonica ${
                      !ticket?.sede?.assistenze || ticket.sede.assistenze?.length === 0
                        ? ''
                        : ticket?.sede?.assistenze?.[0].sospesa &&
                            (ticket?.sede?.assistenze?.[0]?.telefonica?.nome === 'STANDARD' ||
                              ticket?.sede?.assistenze?.[0]?.telefonica?.nome === 'PRIME')
                          ? 'sospesa'
                          : (ticket?.sede?.assistenze?.[0].telefonica?.nome?.toLowerCase() ?? '')
                    }`}
                    defaultValue={
                      !ticket?.sede?.assistenze || ticket.sede.assistenze?.length === 0
                        ? ''
                        : ticket?.sede?.assistenze?.[0].sospesa &&
                            (ticket?.sede?.assistenze?.[0]?.telefonica?.nome === 'STANDARD' ||
                              ticket?.sede?.assistenze?.[0]?.telefonica?.nome === 'PRIME')
                          ? 'SOSPESA'
                          : (ticket?.sede?.assistenze?.[0].telefonica?.nome ?? '')
                    }
                  ></TextBox>
                </SimpleItem>
                <SimpleItem
                  label={{ text: 'Responsabile Tecnico' }}
                  editorType="dxTagBox"
                  editorOptions={{
                    dataSource: ArraySourceComposer(
                      'id',
                      ticket?.sede?.contatti_aziendali?.filter((contatto) => contatto.responsabile_tecnico) ?? [],
                    ),
                    displayExpr: 'fullname',
                    valueExpr: 'id',
                    value:
                      ticket?.sede?.contatti_aziendali
                        ?.filter((contatto) => contatto.responsabile_tecnico)
                        ?.map((contatto) => contatto.id) ?? [],
                    readOnly: true,
                    showSelectionControls: false,
                    showClearButton: false,
                    searchEnabled: false,
                    placeholder: '',
                    tagTemplate: (itemData: contatto_aziendale, itemElement: any) => {
                      const tagContent = `${itemData.fullname ?? ''}`

                      itemElement.innerHTML = `
                        <div class="dx-tag-content" style="cursor: pointer; display: flex; align-items: center;">
                          <span>${tagContent}</span>
                          <div class="dx-tag-remove-button" style="margin-left: 4px;"></div>
                        </div>
                      `
                      // Add click handler to the span only (not the remove button)
                      const tagTextElement = itemElement.querySelector('.dx-tag-content span')
                      tagTextElement?.addEventListener('click', (e: MouseEvent) => {
                        e.stopPropagation()
                        console.log('Tag clicked:', itemData)
                        // Navigate to customization or open in new tab
                        window.open(`/clients/${currentCompany?.id}/contacts/people`, '_blank')
                      })

                      // Add hover effect
                      tagTextElement?.addEventListener('mouseenter', (e: MouseEvent) => {
                        ;(e.target as HTMLElement).style.fontWeight = 'bold'
                      })

                      tagTextElement?.addEventListener('mouseleave', (e: MouseEvent) => {
                        ;(e.target as HTMLElement).style.fontWeight = 'normal'
                      })
                    },
                  }}
                />
              </GroupItem>
              <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 4 }}>
                <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 2 }} colSpan={3}>
                  <SimpleItem dataField="leg_contatto" editorType="dxTextBox" label={{ text: 'Contatto' }}>
                    <RequiredRule message="Contatto obbligatorio"></RequiredRule>
                  </SimpleItem>
                  <SimpleItem
                    dataField="leg_email_contatto"
                    editorType="dxTextBox"
                    label={{ text: 'Email Riferimento' }}
                    editorOptions={{
                      onValueChanged: (e: TextBoxTypes.ValueChangedEvent) => {
                        if (e.value && !ticket?.sede && e.previousValue?.id === e.value?.id) {
                          service
                            .contatto_aziendale()
                            .query((builder, contatto) => {
                              builder.filter(contatto.emails.any((email) => email.email.eq(e.value)))
                              builder.expanding('sede', (sedeBuilder, sede) => {
                                sedeBuilder.expanding('azienda', (aziendaBuilder, azienda) => {
                                  aziendaBuilder.select('id', 'nome')
                                })
                                sedeBuilder.select('azienda')
                              })
                              builder.select('sede')
                            })
                            .then((response) => {
                              if (response.data.value.length > 0) {
                                setCurrentCompany(response.data.value[0].sede?.azienda)
                                setCurrentCompanyChanged(true)
                              }
                            })
                        }
                      },
                    }}
                  >
                    <RequiredRule message="Email riferimento obbligatoria"></RequiredRule>
                    <EmailRule message="Inserire email riferimento valida"></EmailRule>
                  </SimpleItem>
                </GroupItem>
                <GroupItem colCount={1} colSpan={1}>
                  <SimpleItem
                    dataField="leg_phone_contatto"
                    editorType="dxTextBox"
                    label={{ text: 'Telefono' }}
                    editorOptions={{
                      buttons: [
                        customButton('call', (e: ClickEvent) => {
                          if (ticket?.leg_phone_contatto)
                            window.open(`tel:${ticket?.leg_phone_contatto.replace(/\s+/g, '')}`, '_self')
                          else notify('Nessun numero di telefono da chiamare.', 'warning', 2000)
                        }),
                      ],
                    }}
                  ></SimpleItem>
                </GroupItem>
              </GroupItem>
              <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 4 }}>
                <SimpleItem
                  colSpan={3}
                  dataField="email_addizionali"
                  label={{ text: `Email Addizionali` }}
                  editorType="dxTagBox"
                  editorOptions={{
                    dataSource: ArraySourceComposer('email', additionalEmailDataSourceItems),
                    placeholder: 'Inserisci email addizionali...',
                    acceptCustomValue: true,
                    displayExpr: 'email',
                    openOnFieldClick: true,
                    searchEnabled: true,
                    searchExpr: 'email',
                    showSelectionControls: true,
                    onValueChanged: (e: ValueChangedEventTagBox) => {
                      console.log('onValueChanged', e.previousValue, e.value)
                    },
                    onCustomItemCreating: (e: TagBoxTypes.CustomItemCreatingEvent) => {
                      e.customItem = resolveAdditionalEmailCustomItem({
                        text: e.text,
                        referenceEmail: ticket?.leg_email_contatto,
                        currentValues: (e.component.option().value ?? []) as email_aziendale[],
                        dataSourceItems: additionalEmailDataSourceItems,
                      })
                    },
                  }}
                ></SimpleItem>
                <SimpleItem dataField="customer_id" editorType="dxTextBox" label={{ text: 'Customer ID' }}></SimpleItem>
              </GroupItem>
            </GroupItem>
            <GroupItem caption="Dati Ticket">
              <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 3 }}>
                <SimpleItem
                  dataField="stato"
                  label={{ text: 'Stato' }}
                  editorType="dxLookup"
                  editorOptions={{
                    dataSource: ArraySourceComposer(
                      'id',
                      creating
                        ? states.filter((state: stato_ticket) => {
                            return state.id === StatoTicket.APERTO
                          })
                        : states,
                    ),
                    readOnly: true,
                    placeholder: 'Seleziona lo stato...',
                    displayExpr: 'nome',
                    searchEnabled: true,
                    dropDownCentered: true,
                    dropDownOptions: {
                      showTitle: true,
                      title: 'Selezionare lo stato',
                      hideOnOutsideClick: true,
                    },
                    onValueChanged: (e: ValueChangedEvent) => {
                      if (e.previousValue?.id === e.value?.id) return
                      console.log('STATE EVENT CHANGED', e.previousValue, e.value)
                      if (e.value.id === StatoTicket.APERTO) formRef.current?.instance().updateData('assegnato', null)
                    },
                  }}
                >
                  <RequiredRule message="Stato obbligatorio" />
                </SimpleItem>
                <SimpleItem
                  dataField="assegnato"
                  label={{ text: 'Assegnato' }}
                  editorType="dxLookup"
                  editorOptions={{
                    dataSource: ArraySourceComposer('id', employees),
                    placeholder: 'Seleziona utente...',
                    displayExpr: 'fullname',
                    searchEnabled: true,
                    dropDownCentered: true,
                    readOnly: true,
                    dropDownOptions: {
                      showTitle: true,
                      title: `Selezionare l'utente`,
                      hideOnOutsideClick: true,
                    },
                    showClearButton: true,
                  }}
                ></SimpleItem>
                <SimpleItem
                  dataField="incaricato"
                  label={{ text: 'Incaricato' }}
                  editorType="dxLookup"
                  editorOptions={{
                    dataSource: ArraySourceComposer('id', employees),
                    placeholder: 'Seleziona utente...',
                    displayExpr: 'fullname',
                    searchEnabled: true,
                    dropDownCentered: true,
                    readOnly: true,
                    dropDownOptions: {
                      showTitle: true,
                      title: `Selezionare l'utente`,
                      hideOnOutsideClick: true,
                    },
                    showClearButton: true,
                  }}
                ></SimpleItem>
              </GroupItem>
              <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 4 }}>
                <SimpleItem
                  dataField="tipologia_ticket"
                  label={{ text: 'Categoria' }}
                  editorType="dxLookup"
                  editorOptions={{
                    dataSource: ArraySourceComposer('id', categories),
                    placeholder: 'Seleziona categoria...',
                    displayExpr: 'nome',
                    searchEnabled: true,
                    dropDownCentered: true,
                    dropDownOptions: {
                      showTitle: true,
                      title: `Selezionare la categoria`,
                      hideOnOutsideClick: true,
                    },
                  }}
                >
                  <RequiredRule message="Categoria obbligatoria" />
                </SimpleItem>
                <SimpleItem
                  dataField="durata"
                  editorType="dxNumberBox"
                  label={{ text: 'Durata' }}
                  editorOptions={{
                    showSpinButtons: true,
                    min: 0,
                    step: 1,
                  }}
                >
                  <RequiredRule message="Durata obbligatoria"></RequiredRule>
                </SimpleItem>
                <SimpleItem dataField="urgente" label={{ text: 'Urgente' }} editorType="dxSwitch" />
                <SimpleItem dataField="attesa" label={{ text: 'Attesa' }} editorType="dxSwitch" />
              </GroupItem>
              <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 2 }}>
                <SimpleItem label={{ text: `Piattaforma` }}>
                  <DropDownBoxDataGridItem
                    object={ticket?.piattaforma}
                    values={platforms}
                    label="Piattaforma"
                    displayExprField={'nome'}
                    formValidation="ticketValidation"
                    columns={[
                      { dataField: 'nome', width: '200' },
                      { dataField: 'note', width: '800' },
                    ]}
                    readOnly={
                      !!(
                        !isRoleAllowed ||
                        (!creating && ticket?.stato?.id === StatoTicket.APERTO) ||
                        (ticket?.assegnato && ticket?.assegnato.email !== userInfo?.email)
                      )
                    }
                    onChange={useCallback(
                      (newValue: any) => {
                        updateTicketField('piattaforma', newValue)
                        const currentTicket = getCurrentTicketData()
                        refreshProblemArgumentsForSelection(
                          currentTicket?.applicazione ? currentTicket.applicazione.nome : null,
                          newValue ? newValue.nome : null,
                        )
                      },
                      [getCurrentTicketData, refreshProblemArgumentsForSelection, updateTicketField],
                    )}
                    validationRules={[
                      {
                        type: 'custom',
                        message: `Piattaforma diversa da 'Non Presente' se applicazione uguale a 'Non Presente' quando tipologia è 'ASSISTENZA TECNICA'`,
                        reevaluate: true,
                        validationCallback: (options: ValidationCallbackData) => {
                          const piattaforma: piattaforma_ticket = options.value
                          return piattaforma &&
                            piattaforma.nome === ticket?.applicazione?.nome &&
                            piattaforma.nome === '------- non presente -------' &&
                            ticket?.tipologia_ticket?.id === TipologiaTicket.ASSISTENZA_TECNICA
                            ? false
                            : true
                        },
                      },
                      {
                        type: 'custom',
                        message: `Piattaforma obbligatoria se stato 'COMPLETATO'`,
                        validationCallback: ({ value }: ValidationCallbackData) => {
                          return ticket?.stato?.id === StatoTicket.COMPLETATO &&
                            ticket?.tipologia_ticket?.id === TipologiaTicket.ASSISTENZA_TECNICA &&
                            !value
                            ? false
                            : true
                        },
                      },
                    ]}
                  ></DropDownBoxDataGridItem>
                </SimpleItem>
                <SimpleItem label={{ text: `Software Qs` }}>
                  <DropDownBoxDataGridItem
                    object={ticket?.applicazione}
                    values={applications}
                    label="Software Qs"
                    displayExprField={'nome'}
                    formValidation="ticketValidation"
                    columns={[
                      { dataField: 'nome', width: '200' },
                      { dataField: 'note', width: '200' },
                    ]}
                    readOnly={
                      !!(
                        !isRoleAllowed ||
                        (!creating && ticket?.stato?.id === StatoTicket.APERTO) ||
                        (ticket?.assegnato && ticket?.assegnato.email !== userInfo?.email)
                      )
                    }
                    onChange={useCallback(
                      (newValue: any) => {
                        updateTicketField('applicazione', newValue)
                        const currentTicket = getCurrentTicketData()
                        refreshProblemArgumentsForSelection(
                          newValue ? newValue.nome : null,
                          currentTicket?.piattaforma ? currentTicket.piattaforma.nome : null,
                        )
                      },
                      [getCurrentTicketData, refreshProblemArgumentsForSelection, updateTicketField],
                    )}
                    validationRules={[
                      {
                        type: 'custom',
                        message: `Software Qs diverso da 'Non Presente' se piattaforma uguale a 'Non Presente' quando tipologia è 'ASSISTENZA TECNICA'`,
                        reevaluate: true,
                        validationCallback: (options: ValidationCallbackData) => {
                          const applicazione: qs_supporto_applicazione = options.value
                          return applicazione &&
                            applicazione.nome === ticket?.piattaforma?.nome &&
                            applicazione.nome === '------- non presente -------' &&
                            ticket?.tipologia_ticket?.id === TipologiaTicket.ASSISTENZA_TECNICA
                            ? false
                            : true
                        },
                      },
                      {
                        type: 'custom',
                        message: `Software QS obbligatorio se stato 'COMPLETATO'`,
                        validationCallback: ({ value }: ValidationCallbackData) => {
                          return ticket?.stato?.id === StatoTicket.COMPLETATO &&
                            ticket?.tipologia_ticket?.id === TipologiaTicket.ASSISTENZA_TECNICA &&
                            !value
                            ? false
                            : true
                        },
                      },
                    ]}
                  ></DropDownBoxDataGridItem>
                </SimpleItem>
                <SimpleItem label={{ text: `Categoria Problema` }}>
                  <DropDownBoxDataGridItem
                    object={ticket?.tipologia_problema}
                    values={problemCategories}
                    label="Categoria Problema"
                    displayExprField={'nome'}
                    formValidation="ticketValidation"
                    columns={[
                      { dataField: 'nome', width: '250' },
                      { dataField: 'note', width: '300' },
                    ]}
                    readOnly={
                      !!(
                        !isRoleAllowed ||
                        (!creating && ticket?.stato?.id === StatoTicket.APERTO) ||
                        (ticket?.assegnato && ticket?.assegnato.email !== userInfo?.email)
                      )
                    }
                    onChange={(newValue: any) => {
                      updateTicketField('tipologia_problema', newValue)
                    }}
                    validationRules={[
                      {
                        type: 'custom',
                        message: `Categoria Problema obbligatoria se stato 'COMPLETATO'`,
                        validationCallback: ({ value }: ValidationCallbackData) => {
                          return ticket?.stato?.id === StatoTicket.COMPLETATO &&
                            ticket?.tipologia_ticket?.id === TipologiaTicket.ASSISTENZA_TECNICA &&
                            !value
                            ? false
                            : true
                        },
                      },
                    ]}
                  ></DropDownBoxDataGridItem>
                </SimpleItem>
                <SimpleItem label={{ text: `Argomento Problema` }}>
                  <DropDownBoxDataGridItem
                    object={ticket?.argomento_problema}
                    values={problemArgumentsList}
                    label="Argomento Problema"
                    displayExprField={'nome'}
                    formValidation="ticketValidation"
                    columns={[
                      { dataField: 'nome', width: '250' },
                      { dataField: 'note', width: '800' },
                    ]}
                    readOnly={
                      !!(
                        !isRoleAllowed ||
                        (!creating && ticket?.stato?.id === StatoTicket.APERTO) ||
                        (ticket?.assegnato && ticket?.assegnato.email !== userInfo?.email)
                      )
                    }
                    onChange={(newValue: any) => {
                      updateTicketField('argomento_problema', newValue)
                    }}
                    validationRules={[
                      {
                        type: 'custom',
                        message: `Argomento Problema obbligatorio se stato 'COMPLETATO'`,
                        validationCallback: ({ value }: ValidationCallbackData) => {
                          return ticket?.stato?.id === StatoTicket.COMPLETATO &&
                            ticket?.tipologia_ticket?.id === TipologiaTicket.ASSISTENZA_TECNICA &&
                            !value
                            ? false
                            : true
                        },
                      },
                    ]}
                  ></DropDownBoxDataGridItem>
                </SimpleItem>
              </GroupItem>
            </GroupItem>
          </GroupItem>
          <GroupItem colCount={1}>
            <TabbedItem>
              <TabPanelOptions
                animationEnabled={true}
                swipeEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall}
                deferRendering={true}
                scrollingEnabled={true}
              />
              {!creating && (
                <Tab title="Note">
                  <SimpleItem
                    editorType={'dxHtmlEditor'}
                    label={{ visible: false }}
                    editorOptions={{ readOnly: true }}
                    render={() => {
                      return (
                        <div className="ticket-notes-container">
                          <ScrollView height={550} width={'100%'} direction="vertical">
                            {renderTicketNotes()}
                          </ScrollView>
                        </div>
                      )
                    }}
                  ></SimpleItem>
                </Tab>
              )}
              {/* <Tab title="Problema"> */}
              <Tab title="Motivo">
                <SimpleItem
                  dataField="descrizione"
                  label={{ visible: false }}
                  editorType="dxHtmlEditor"
                  editorOptions={{
                    stylingMode: 'filled',
                    valueType: 'html',
                    toolbar: creating ? toolbarHtmlEditor(currentScreenSize) : undefined,
                    readOnly: !creating,
                    mediaResizing: {
                      enabled: true,
                    },
                    imageUpload: {
                      tabs: ['file', 'url'],
                      fileUploadMode: 'base64',
                    },
                    height: 550,
                    onValueChanged: onValueChangedHtmlEditor,
                    onInitialized: onInitializedHtmlEditor,
                  }}
                >
                  <CustomRule
                    message="Problema obbligatorio"
                    validationCallback={(options: ValidationCallbackData) => {
                      if (creating) return options.value ? true : false
                      else return true
                    }}
                  ></CustomRule>
                </SimpleItem>
              </Tab>
              {!creating && (
                <Tab title="Allegati">
                  <AttachmentsManager
                    fileManagerRef={fileManagerRef}
                    folderUrl={ticket?.attachmentsFolderUrl}
                    preliminarPermissionsAction={async () => {
                      return await getTicketAttachmentPermissions(client, ticket?.id, {
                        objectId: ticket?.id,
                        path: ticket?.attachmentsFolderUrl ?? '',
                      })
                    }}
                    createDirectoryAction={async (rootFolderPath: string, name: string, conflictBehavior: string) => {
                      return await createTicketAttachmentFolder(client, ticket?.id ?? 0, {
                        rootFolder: rootFolderPath,
                        newFolderName: name,
                        conflictBehavior,
                        objectId: ticket?.id,
                      })
                    }}
                  />
                </Tab>
              )}
              {!creating && (
                <Tab title="Eventi">
                  <TicketEditorEvents ticket={ticket}></TicketEditorEvents>
                </Tab>
              )}
              {!creating && (
                <Tab title="Dump">
                  <TicketEditorDumps dumps={ticket?.dumps}></TicketEditorDumps>
                </Tab>
              )}
              {/* {ticket?.soluzione && ( */}
              {ticket?.stato?.id === StatoTicket.COMPLETATO && (
                <Tab title="Soluzione">
                  <SimpleItem
                    dataField="soluzione"
                    label={{ visible: false }}
                    editorType="dxHtmlEditor"
                    editorOptions={{
                      stylingMode: 'filled',
                      valueType: 'html',
                      readOnly: true,
                      mediaResizing: {
                        enabled: true,
                      },
                      imageUpload: {
                        tabs: ['file', 'url'],
                        fileUploadMode: 'base64',
                      },
                      height: 550,
                    }}
                  ></SimpleItem>
                </Tab>
              )}
              {ticket?.stato?.id === StatoTicket.COMPLETATO && ticket.rating && (
                <Tab title="Rating">
                  <SimpleItem
                    dataField="rating_note"
                    label={{ visible: false }}
                    editorType="dxHtmlEditor"
                    editorOptions={{
                      stylingMode: 'filled',
                      valueType: 'html',
                      readOnly: true,
                      mediaResizing: {
                        enabled: true,
                      },
                      imageUpload: {
                        tabs: ['file', 'url'],
                        fileUploadMode: 'base64',
                      },
                      height: 400,
                    }}
                  ></SimpleItem>
                </Tab>
              )}
            </TabbedItem>
          </GroupItem>
        </GroupItem>
        <GroupItem visible={isRoleAllowed} cssClass="last-group">
          <GroupItem cssClass="buttons-group" colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: creating ? 2 : 5 }}>
            {!creating && (
              <>
                <SimpleItem>
                  <DropDownButton
                    id="ddbAzioni-ticket"
                    text="Azioni"
                    useItemTextAsTitle={false}
                    items={[
                      {
                        icon: 'email',
                        text: 'Invia nota',
                        visible:
                          isUserRoleAllowed(userInfo?.roles, [
                            Roles.GlobalAdministrator,
                            Roles.TechDeveloper,
                            Roles.Administrator,
                            Roles.Supervisor,
                          ]) &&
                          !creating &&
                          ticket?.stato?.id === StatoTicket.ASSEGNATO &&
                          ticket?.assegnato &&
                          ticket?.assegnato.email === userInfo?.email
                            ? true
                            : false,
                        onClick: (e: ClickEvent) => {
                          const validationResult = formRef.current?.instance().validate()
                          if (!validationResult?.isValid) return
                          if (formRef.current?.instance().option().isDirty)
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                          else {
                            setPopup({
                              visible: true,
                              action: TicketActions.SendTicketNote,
                              note: createTicketNoteDraft(ticketNoteContext, {
                                testo: readTicketEditorDraft('send-note', ticket?.id),
                                tag: uuidv4(),
                              }),
                              ticket,
                            })
                            popupActionRef.current?.instance().show()
                          }
                        },
                      },
                      {
                        icon: 'comment',
                        text: 'Nota interna',
                        visible:
                          isUserRoleAllowed(userInfo?.roles, [
                            Roles.GlobalAdministrator,
                            Roles.TechDeveloper,
                            Roles.Administrator,
                            Roles.Supervisor,
                          ]) && !creating,
                        onClick: (e: ClickEvent) => {
                          if (formRef.current?.instance().option().isDirty)
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                          else {
                            setPopup({
                              visible: true,
                              action: TicketActions.CreateTicketNote,
                              note: createTicketNoteDraft(ticketNoteContext, {
                                testo: readTicketEditorDraft('internal-note', ticket?.id),
                                tag: uuidv4(),
                              }),
                              ticket,
                            })
                            popupActionRef.current?.instance().show()
                          }
                        },
                      },
                      {
                        icon: 'user',
                        text: `Assegna a me`,
                        visible:
                          isUserRoleAllowed(userInfo?.roles, [
                            Roles.GlobalAdministrator,
                            Roles.TechDeveloper,
                            Roles.Supervisor,
                            Roles.Administrator,
                          ]) &&
                          ticket?.stato?.id !== StatoTicket.COMPLETATO &&
                          ticket?.stato?.id !== StatoTicket.ELIMINATO &&
                          !ticket?.assegnato,
                        onClick: (e: ClickEvent) => {
                          if (formRef.current?.instance().option().isDirty) {
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                            return
                          }
                          const currentUser = impiegatiData?.find((u) => u.email === userInfo?.email)
                          const userId = currentUser?.id
                          if (userId && ticket?.id && currentUser) {
                            const newAssegnatoState = states.find((s) => s.id === StatoTicket.ASSEGNATO)
                            ticket.assegnato = currentUser // Mutate prop - Handled by optimistic update in hook
                            if (newAssegnatoState) ticket.stato = newAssegnatoState // Mutate prop - Handled by optimistic update in hook
                            formRef.current?.instance().updateData('assegnato', currentUser)
                            formRef.current?.instance().updateData('stato', newAssegnatoState)
                            formRef.current?.instance().option('isDirty', false)
                            assignToMutation.mutate({
                              ticketRecordId: ticket.id,
                              assigneeUser: currentUser,
                              assignedStato: newAssegnatoState,
                            } satisfies AssignToMutationParams)
                          }
                        },
                      },
                      {
                        icon: 'user',
                        text: `Assegna a`,
                        visible:
                          isUserRoleAllowed(userInfo?.roles, [
                            Roles.GlobalAdministrator,
                            Roles.Supervisor,
                            Roles.TechDeveloper,
                            Roles.Administrator,
                          ]) &&
                          ticket?.stato?.id !== StatoTicket.COMPLETATO &&
                          ticket?.stato?.id !== StatoTicket.ELIMINATO &&
                          !ticket?.assegnato,
                        onClick: (e: ClickEvent) => {
                          if (formRef.current?.instance().option().isDirty)
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                          else popupAssignRef.current?.instance().show()
                        },
                      },
                      {
                        icon: 'cart',
                        text: `Prendi in carico`,
                        onClick: async (e: ClickEvent) => {
                          if (formRef.current?.instance().option().isDirty)
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                          else {
                            const currentUser = impiegatiData?.find((u) => u.email === userInfo?.email)
                            const needsConfirmation =
                              ticket?.assegnato && ticket?.assegnato.email !== userInfo?.email
                                ? await confirm(
                                    ticket?.incaricato && ticket?.incaricato.fullname !== userInfo?.name
                                      ? `<i>Il ticket <b>${ticket.ded_Dis}</b> è in carico all'utente ${ticket.incaricato.fullname}. Si desidera procedere ugualmente?</i>`
                                      : `<i>Il ticket <b>${ticket.ded_Dis}</b> è assegnato all'utente ${ticket.assegnato?.fullname}. Si desidera procedere ugualmente?</i>`,
                                    'Presa in Carico Ticket',
                                  )
                                : true
                            if (currentUser && needsConfirmation && ticket) {
                              const wasNotAssigned = !ticket.assegnato
                              const newAssegnatoState = states.find((s) => s.id === StatoTicket.ASSEGNATO)

                              let newAssegnatoForForm = ticket.assegnato
                              let newStatoForForm = ticket.stato

                              ticket.incaricato = currentUser // Mutate prop
                              if (wasNotAssigned) {
                                ticket.assegnato = currentUser // Mutate prop
                                newAssegnatoForForm = currentUser
                                if (newAssegnatoState) {
                                  ticket.stato = newAssegnatoState // Mutate prop
                                  newStatoForForm = newAssegnatoState
                                }
                              }
                              formRef.current?.instance().updateData('incaricato', currentUser)
                              formRef.current?.instance().updateData('assegnato', newAssegnatoForForm)
                              formRef.current?.instance().updateData('stato', newStatoForForm)
                              formRef.current?.instance().option('isDirty', false)
                              takeChargeMutation.mutate({ ticket }) // Pass mutated prop
                            }
                          }
                        },
                        visible:
                          isUserRoleAllowed(userInfo?.roles, [
                            Roles.GlobalAdministrator,
                            Roles.TechDeveloper,
                            Roles.Supervisor,
                          ]) &&
                          !creating &&
                          ticket?.stato?.id !== StatoTicket.COMPLETATO &&
                          ticket?.stato?.id !== StatoTicket.ELIMINATO &&
                          !ticket?.incaricato &&
                          (!ticket?.assegnato || ticket.assegnato.email === userInfo?.email)
                            ? true
                            : false,
                      },
                      {
                        icon: 'arrowback',
                        text: 'Rimuovi assegnazione',
                        onClick: async (e: ClickEvent) => {
                          if (formRef.current?.instance().option().isDirty) {
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                            return
                          }
                          const needsConfirmation =
                            ticket?.assegnato && ticket?.assegnato.email !== userInfo?.email
                              ? await confirm(
                                  `<i>Il ticket <b>${ticket.ded_Dis}</b> è assegnato all'utente ${ticket.assegnato?.fullname}. Si desidera procedere ugualmente?</i>`,
                                  'Rimozione Assegnazione Ticket',
                                )
                              : true
                          if (needsConfirmation && ticket) {
                            setLoadPanelVisible(true)
                            try {
                              await removeAssigneeMutation.mutateAsync({ ticket })
                              const openState = states.find((s) => s.id === StatoTicket.APERTO)
                              ticket.assegnato = null
                              ticket.incaricato = null
                              if (openState) ticket.stato = openState
                              formRef.current?.instance().updateData('assegnato', null)
                              formRef.current?.instance().updateData('incaricato', null)
                              formRef.current?.instance().updateData('stato', openState)
                              formRef.current?.instance().option('isDirty', false)
                            } finally {
                              setLoadPanelVisible(false)
                            }
                          }
                        },
                        visible:
                          ticket?.stato?.id !== StatoTicket.COMPLETATO &&
                          ticket?.stato?.id !== StatoTicket.ELIMINATO &&
                          ticket?.assegnato &&
                          (isUserRoleAllowed(userInfo?.roles, [
                            Roles.GlobalAdministrator,
                            Roles.Supervisor,
                            Roles.Administrator,
                          ]) ||
                            (isUserRoleAllowed(userInfo?.roles, [Roles.TechDeveloper]) &&
                              ticket.assegnato.email === userInfo?.email)),
                      },
                      {
                        icon: 'arrowback',
                        text: 'Rilascia',
                        onClick: (e: ClickEvent) => {
                          if (formRef.current?.instance().option().isDirty) {
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                            return
                          }
                          if (ticket) {
                            ticket.incaricato = null // Mutate prop
                            formRef.current?.instance().updateData('incaricato', null)
                            formRef.current?.instance().option('isDirty', false)
                            releaseMutation.mutate({ ticket }) // Pass mutated prop
                          }
                        },
                        visible:
                          isUserRoleAllowed(userInfo?.roles, [
                            Roles.GlobalAdministrator,
                            Roles.TechDeveloper,
                            Roles.Supervisor,
                          ]) &&
                          ticket?.stato?.id !== StatoTicket.COMPLETATO &&
                          ticket?.stato?.id !== StatoTicket.ELIMINATO &&
                          ticket?.incaricato &&
                          ticket?.incaricato.email === userInfo?.email,
                      },
                      {
                        icon: 'check',
                        text: 'Completa',
                        visible:
                          isUserRoleAllowed(userInfo?.roles, [
                            Roles.GlobalAdministrator,
                            Roles.TechDeveloper,
                            Roles.Administrator,
                            Roles.Supervisor,
                          ]) &&
                          !creating &&
                          ticket?.stato?.id === StatoTicket.ASSEGNATO &&
                          ticket?.assegnato &&
                          ticket?.assegnato.email === userInfo?.email
                            ? // && ticket.soluzione
                              true
                            : false,
                        onClick: (e: ClickEvent) => {
                          const currentTicket = getCurrentTicketData()
                          const validationResult = formRef.current?.instance().validate()
                          if (!validationResult?.isValid) return
                          if (formRef.current?.instance().option().isDirty) {
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                            return
                          }
                          if (
                            !currentTicket?.piattaforma &&
                            currentTicket?.tipologia_ticket?.id === TipologiaTicket.ASSISTENZA_TECNICA
                          ) {
                            notify(`Valorizzare piattaforma`, 'warning', 3000)
                            return
                          }
                          if (
                            !currentTicket?.applicazione &&
                            currentTicket?.tipologia_ticket?.id === TipologiaTicket.ASSISTENZA_TECNICA
                          ) {
                            notify(`Valorizzare software Qs`, 'warning', 3000)
                            return
                          }
                          if (
                            !currentTicket?.tipologia_problema &&
                            currentTicket?.tipologia_ticket?.id === TipologiaTicket.ASSISTENZA_TECNICA
                          ) {
                            notify(`Valorizzare categoria problema`, 'warning', 3000)
                            return
                          }
                          if (
                            !currentTicket?.argomento_problema &&
                            currentTicket?.tipologia_ticket?.id === TipologiaTicket.ASSISTENZA_TECNICA
                          ) {
                            notify(`Valorizzare argomento problema`, 'warning', 3000)
                            return
                          }
                          if (!currentTicket) return
                          setPopup({
                            visible: true,
                            action: TicketActions.Complete,
                            complete: {
                              closure: readTicketEditorDraft('closure-note', currentTicket.id) ?? '',
                              solution: readTicketEditorDraft('solution', currentTicket.id) ?? '',
                              ticket: currentTicket,
                            },
                          })
                          popupActionRef.current?.instance().show()
                        },
                      },
                      // {
                      //   icon: 'fas fa-brain',
                      //   text: 'Risolvi',
                      //   visible:
                      //     isUserRoleAllowed(userInfo?.roles, [
                      //       Roles.GlobalAdministrator,
                      //       Roles.TechDeveloper,
                      //       Roles.Administrator,
                      //       Roles.Supervisor,
                      //     ]) &&
                      //     !creating &&
                      //     ticket?.stato?.id === StatoTicket.ASSEGNATO &&
                      //     ticket?.assegnato &&
                      //     ticket?.assegnato.email === userInfo?.email
                      //       ? true
                      //       : false,
                      //   onClick: (e: ClickEvent) => {
                      //     window.open(`/tickets/${ticket?.id}/resolving`, '_blank')
                      //   },
                      // },
                      {
                        icon: 'revert',
                        text: 'Riapri',
                        visible:
                          isUserRoleAllowed(userInfo?.roles, [
                            Roles.GlobalAdministrator,
                            Roles.TechDeveloper,
                            Roles.Supervisor,
                            Roles.Administrator,
                          ]) &&
                          !creating &&
                          (ticket?.stato?.id === StatoTicket.COMPLETATO || ticket?.stato?.id === StatoTicket.ELIMINATO)
                            ? true
                            : false,
                        onClick: (e: ClickEvent) => {
                          if (formRef.current?.instance().option().isDirty)
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                          else {
                            if (ticket?.ded_Dis) {
                              const assegnatoState = states.find((s) => s.id === StatoTicket.ASSEGNATO)
                              // Fields to update on ticket prop
                              if (assegnatoState) ticket.stato = assegnatoState
                              formRef.current?.instance().updateData('stato', assegnatoState)
                              formRef.current?.instance().option('isDirty', false)
                              reopenMutation.mutate({ ticket }) // Pass mutated prop
                            }
                          }
                        },
                      },
                      {
                        icon: 'close',
                        text: 'Elimina',
                        onClick: async (e: ClickEvent) => {
                          if (formRef.current?.instance().option().isDirty) {
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                            return
                          }
                          const confirmation = await confirm(
                            `<i>Si desidera eliminare il ticket <b>${ticket?.ded_Dis}</b> di <b>${ticket?.leg_contatto}</b> dell' azienda <b>${ticket?.sede?.azienda?.nome ?? 'non definita'}</b>?</i>`,
                            'Eliminazione Ticket',
                          )
                          if (confirmation && ticket?.ded_Dis) {
                            const eliminatoState = states.find((s) => s.id === StatoTicket.ELIMINATO)
                            if (eliminatoState) ticket.stato = eliminatoState // Mutate prop
                            formRef.current?.instance().updateData('stato', eliminatoState)
                            formRef.current?.instance().option('isDirty', false)
                            cancelMutation.mutate({ ticket }) // Pass mutated prop
                          }
                        },
                        visible:
                          isUserRoleAllowed(userInfo?.roles, [
                            Roles.GlobalAdministrator,
                            Roles.TechDeveloper,
                            Roles.Supervisor,
                          ]) &&
                          !creating &&
                          ticket?.stato?.id !== StatoTicket.COMPLETATO &&
                          ticket?.stato?.id !== StatoTicket.ELIMINATO &&
                          ticket?.assegnato &&
                          ticket?.assegnato.email === userInfo?.email
                            ? true
                            : false,
                      },
                      {
                        text: 'Invia notifica chiusura',
                        icon: 'bell',
                        visible:
                          isUserRoleAllowed(userInfo?.roles, [
                            Roles.GlobalAdministrator,
                            Roles.TechDeveloper,
                            Roles.Supervisor,
                          ]) &&
                          ticket?.stato?.id !== StatoTicket.COMPLETATO &&
                          ticket?.stato?.id !== StatoTicket.ELIMINATO &&
                          ticket?.assegnato &&
                          ticket.assegnato.email === userInfo?.email,
                        onClick: async () => {
                          if (formRef.current?.instance().option().isDirty)
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                          else {
                            const conditions = [
                              !ticket?.leg_email_contatto
                                ? notify("Email riferimento assente: impossibile eseguire l' azione.", 'warning', 3000)
                                : true,
                              !ticket?.sede
                                ? notify("Sede assente: impossibile eseguire l' azione.", 'warning', 3000)
                                : await confirm(
                                    `<i>Vuoi davvero inviare la notifica di chiusura del ticket <b>${ticket?.ded_Dis}</b>?</i>`,
                                    'Invio Notifica Chiusura Ticket',
                                  ),
                            ]

                            if (conditions.every((condition) => condition === true) && ticket) {
                              sendClosureNoticeMutation.mutate({ ticket })
                            }
                          }
                        },
                      },
                      {
                        text: 'Invia notifica contatto',
                        icon: 'bell',
                        visible:
                          isRoleAllowed &&
                          ticket?.stato?.id !== StatoTicket.COMPLETATO &&
                          ticket?.stato?.id !== StatoTicket.ELIMINATO &&
                          ticket?.assegnato &&
                          ticket.assegnato.email === userInfo?.email,
                        onClick: async () => {
                          if (formRef.current?.instance().option().isDirty)
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                          else {
                            const conditions = [
                              !ticket?.leg_email_contatto
                                ? notify("Email riferimento assente: impossibile eseguire l' azione.", 'warning', 3000)
                                : true,
                              !ticket?.sede
                                ? notify("Sede assente: impossibile eseguire l' azione.", 'warning', 3000)
                                : await confirm(
                                    `<i>Vuoi davvero inviare la notifica di contatto per il ticket <b>${ticket.ded_Dis}</b>?</i>`,
                                    `Invio Notifica Contatto Ticket`,
                                  ),
                            ]
                            if (conditions.every((condition) => condition === true) && ticket) {
                              sendContactNoticeMutation.mutate({ ticket })
                            }
                          }
                        },
                      },
                      {
                        text: 'Imposta richiesta di attività',
                        icon: 'fas fa-hand',
                        visible:
                          isUserRoleAllowed(userInfo?.roles, [
                            Roles.GlobalAdministrator,
                            Roles.TechDeveloper,
                            Roles.Supervisor,
                          ]) &&
                          ticket?.stato?.id !== StatoTicket.COMPLETATO &&
                          ticket?.stato?.id !== StatoTicket.ELIMINATO &&
                          ticket?.tipologia_ticket?.nome !== `RICHIESTA DI ATTIVITA'` &&
                          !ticket?.assegnato,
                        onClick: async () => {
                          const conditions = [
                            await confirm(
                              `<i>Vuoi davvero impostare il ticket <b>${ticket?.ded_Dis}</b> come <b>RICHIESTA DI ATTIVITA'</b>?</i>`,
                              `Imposta Richiesta di Attività`,
                            ),
                          ]
                          if (conditions.every((condition) => condition === true) && ticket) {
                            const type = categories.find((t) => t.nome === `RICHIESTA DI ATTIVITA'`)
                            ticket.tipologia_ticket = type // Mutate prop - Handled by optimistic update in hook
                            formRef.current?.instance().updateData('tipologia_ticket', type)
                            formRef.current?.instance().option('isDirty', false)
                            setActivityRequestMutation.mutate({ ticket })
                          }
                        },
                      },
                      {
                        text: 'Invia notifica assistenza scaduta',
                        icon: 'bell',
                        visible: isRoleAllowed,
                        onClick: async () => {
                          if (formRef.current?.instance().option().isDirty) {
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                            return
                          }
                          const conditions = [
                            !ticket?.leg_email_contatto
                              ? notify("Email riferimento assente: impossibile eseguire l' azione.", 'warning', 3000)
                              : true,
                            !ticket?.sede
                              ? notify("Sede assente: impossibile eseguire l' azione.", 'warning', 3000)
                              : await confirm(
                                  `<i>Vuoi davvero inviare la notifica di assistenza scaduta per il ticket <b>${ticket.ded_Dis}</b>?</i>`,
                                  `Invio Notifica Assistenza Scaduta Ticket`,
                                ),
                          ]
                          if (conditions.every((condition) => condition === true) && ticket) {
                            sendSupportExpiredNoticeMutation.mutate({ ticket })
                          }
                        },
                      },
                      {
                        text: 'Invia notifica assistenza sospesa',
                        icon: 'bell',
                        visible: isRoleAllowed,
                        onClick: async () => {
                          if (formRef.current?.instance().option().isDirty) {
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                            return
                          }
                          const conditions = [
                            !ticket?.leg_email_contatto
                              ? notify("Email riferimento assente: impossibile eseguire l' azione.", 'warning', 3000)
                              : true,
                            !ticket?.sede
                              ? notify("Sede assente: impossibile eseguire l' azione.", 'warning', 3000)
                              : await confirm(
                                  `<i>Vuoi davvero inviare la notifica di assistenza sospesa per il ticket <b>${ticket.ded_Dis}</b>?</i>`,
                                  `Invio Notifica Assistenza Sospesa Ticket`,
                                ),
                          ]
                          if (conditions.every((condition) => condition === true) && ticket) {
                            sendSupportSuspendedNoticeMutation.mutate({ ticket })
                          }
                        },
                      },
                      {
                        text: 'Invia notifica altro fornitore',
                        icon: 'bell',
                        visible: isRoleAllowed,
                        onClick: async () => {
                          if (formRef.current?.instance().option().isDirty) {
                            notify(
                              `Salvare o annullare le modifiche sul ticket prima di procedere con l'azione.`,
                              'warning',
                              3000,
                            )
                            return
                          }
                          const conditions = [
                            !ticket?.leg_email_contatto
                              ? notify("Email riferimento assente: impossibile eseguire l' azione.", 'warning', 3000)
                              : true,
                            !ticket?.sede
                              ? notify("Sede assente: impossibile eseguire l' azione.", 'warning', 3000)
                              : await confirm(
                                  `<i>Vuoi davvero inviare la notifica di altro fornitore per il ticket <b>${ticket.ded_Dis}</b>?</i>`,
                                  `Invio Notifica Altro Fornitore Ticket`,
                                ),
                          ]
                          if (conditions.every((condition) => condition === true) && ticket) {
                            sendOtherVendorNoticeMutation.mutate({ ticket })
                          }
                        },
                      },
                    ]}
                    dropDownOptions={{ width: '250' }}
                    width={'150px'}
                  />
                </SimpleItem>
                <ButtonItem name="btn-duplicate">
                  <ButtonOptions
                    text="Duplica"
                    icon="copy"
                    useSubmitBehavior={false}
                    width={'150px'}
                    onClick={(e: ClickEvent) => {
                      const result = confirm(
                        `<i>Vuoi davvero duplicare il ticket <b>${ticket?.ded_Dis}</b>?</i>`,
                        'Duplicazione ticket',
                      )
                      result.then((dialogResult) => {
                        if (dialogResult === false) return
                        setLoadPanelVisible(true)
                        duplicateTicketWithApi(client, ticket?.id ?? 0)
                          .then((response: AxiosResponse) => {
                            console.log('response', response.data)
                            window.open(`/tickets/${response.data.ticketRecordId}`, '_blank')
                          })
                          .catch((error: AxiosError) => {
                            notify(`Errore duplicazione ticket. Dettagli : ${error}`, 'error', 2000)
                          })
                          .finally(() => {
                            setLoadPanelVisible(false)
                          })
                      })
                    }}
                  />
                </ButtonItem>
              </>
            )}

            <ButtonItem name="btn-historic">
              <ButtonOptions
                text="Storico"
                icon="eyeopen"
                useSubmitBehavior={false}
                width={'150px'}
                onClick={(e: ClickEvent) => {
                  window.open(`/tickets/historic`, '_blank')
                }}
              />
            </ButtonItem>
            <ButtonItem name="btn-save">
              <ButtonOptions
                disabled={!formRef.current?.instance().option().isDirty}
                icon="save"
                text="Salva"
                width="150px"
                onClick={async () => {
                  const validationResult = formRef.current?.instance().validate()
                  if (!validationResult?.isValid) return
                  const currentTicket = getCurrentTicketData()
                  console.log('validation ok', currentTicket)
                  if (currentTicket) {
                    console.log('formData', currentTicket)
                    onSaveTicket?.(currentTicket)
                  } else console.error('no data tu submit')
                }}
              />
            </ButtonItem>
            {!creating && (
              <ButtonItem name="btn-cancel">
                <ButtonOptions
                  disabled={!formRef.current?.instance().option().isDirty}
                  width="150px"
                  text="Annulla"
                  icon="undo"
                  onClick={() => {
                    onResetTicket?.()
                  }}
                />
              </ButtonItem>
            )}
          </GroupItem>
        </GroupItem>
      </DXForm>
      <Popup
        width={300}
        height={'auto'}
        hideOnOutsideClick={true}
        showCloseButton={true}
        deferRendering={true}
        title={'Assegna a'}
        ref={popupAssignRef}
        className="assignto-popup-content"
        resizeEnabled={false}
      >
        <AssignSelector
          ticket={ticket}
          popupAssignRef={popupAssignRef}
          clientId={clientId}
          onAssigned={(assignee) => {
            if (ticket?.ded_Dis && assignee) {
              const newAssegnatoState = states.find((s) => s.id === StatoTicket.ASSEGNATO)
              ticket.assegnato = assignee // Mutate prop - Handled by optimistic update in hook
              if (newAssegnatoState) ticket.stato = newAssegnatoState // Mutate prop - Handled by optimistic update in hook
              formRef.current?.instance().updateData('assegnato', assignee)
              formRef.current?.instance().updateData('stato', newAssegnatoState)
              formRef.current?.instance().option('isDirty', false)
            }
          }}
        />
      </Popup>
      <TicketEditorPopup
        refPopup={popupActionRef}
        popup={popup}
        userInfo={userInfo}
        onRefreshTicket={onRefreshTicket}
      ></TicketEditorPopup>
      <FormPopupClientNote
        data={ticket?.sede}
        refPopup={popupNoteClienteRef}
        refreshDataSourceSites={() => {
          if (currentCompany) {
            getCompanySites(currentCompany?.id).then((sites: sede[]) => {
              console.log('SITES', sites)
              setSediList(sites)
              formRef.current?.instance().updateData(
                'sede',
                sites.find((site: sede) => {
                  return site.id === ticket?.sede?.id
                }),
              )
            })
          }
        }}
        isRoleAllowed={isUserRoleAllowed(userInfo?.roles, [
          Roles.TechDeveloper,
          Roles.Sales,
          Roles.GlobalAdministrator,
          Roles.ExternalSales,
          Roles.Administrator,
        ])}
      ></FormPopupClientNote>
    </>
  )
}

export default TicketEditorForm
